import { createNode } from '@glueapp/graph-types';
import { isShapeValue } from '@glueapp/graph-types';

export const BuildList = createNode({
    id: 'Build List',
    name: 'Build List',
    description: 'Builds a list from individual element values. Use the + and - buttons to control the number of elements.',
    aiDescription: [
        'Builds a list from individually connected element values. Has a computeShape that creates dynamic "Element N" input ports.',
        'Setup steps:',
        '1. Set the "Element Type" input port (a shape value) to define the type of list elements:',
        '   glue set-value <id> "Element Type" \'{"isShapeValue":true,"type":{"kind":"string"}}\'',
        '   Without this, there are no output ports — the "List" output only appears after a shape is set.',
        '2. Add element ports via state. The state key is "buildListElements" (array of integer indices, one per port — the interface component manages it).',
        '   glue set-state <id> buildListElements \'[0,1,2]\'',
        '   This creates input ports "Element 0", "Element 1", "Element 2" whose types match the Element Type shape.',
        '3. Connect or set values on each "Element N" port.',
        '4. Output: "List" — an array whose subType matches the Element Type shape.',
        'Use glue shape <id> after setting state and Element Type to verify the dynamic ports.',
    ].join('\n'),
    category: 'List',
    color: '#778ed5',
    interfaceComponentId: 'build-list-editor',

    inPorts: {
        'Element Type': {
            kind: 'shape',
            restriction: undefined,
        },
    },

    outPorts: {
        List: {
            kind: 'array',
            subType: { kind: 'string' },
        },
    },

    computeShape: input => {
        const shapePortValue = input.portValues['Element Type'];

        // Derive element ports from state, falling back to preResolvedInputPorts for backward compat
        const elementIndices = (input.state?.buildListElements as number[]) || [];
        const elementPorts =
            elementIndices.length > 0
                ? elementIndices.map(index => ({ name: `Element ${index}`, type: { kind: 'string' as const } }))
                : input.preResolvedInputPorts.filter(p => /^Element \d+$/.test(p.name));

        if (isShapeValue(shapePortValue)) {
            const elementType = shapePortValue.type;

            // Retype element ports to the selected element type
            const retypedElementPorts = elementPorts.map(p => ({
                name: p.name,
                type: elementType,
            }));

            return {
                inPorts: [...input.nodeInPorts, ...retypedElementPorts],
                outPorts: [{ name: 'List', type: { kind: 'array' as const, subType: elementType } }],
            };
        }

        // No shape set: keep element ports with placeholder types, hide output
        return {
            inPorts: [...input.nodeInPorts, ...elementPorts],
            outPorts: [],
        };
    },

    execute: function (input) {
        // Collect all Element N port values, sort by N
        const elements: { index: number; value: any }[] = [];

        for (const portName in input.inPorts) {
            const match = portName.match(/^Element (\d+)$/);
            if (match) {
                elements.push({
                    index: parseInt(match[1], 10),
                    value: (input.inPorts as any)[portName],
                });
            }
        }

        elements.sort((a, b) => a.index - b.index);

        return {
            output: {
                List: elements.map(e => e.value),
            },
        };
    },
});
